<script setup>
import {
  computed,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch
} from 'vue';
import emitter from 'tiny-emitter/instance';

import ParsingItem from '@/views/bulk-parsing/ParsingItem.vue';
import BulkCvResolutionModal from '@/views/bulk-parsing/BulkCvResolutionModal.vue';
import CustomModal from '@/views/common/text-editors/CustomModal.vue';
import Spinner from '@/views/common/components/Spinner.vue';
import useBulkCvBatches, {
  BULK_CV_ACTIONS
} from '@/composables/recruitments/useBulkCvBatches';
import useBulkCvRealtime from '@/composables/recruitments/useBulkCvRealtime';
import useToast from '@/composables/app/useToast';

const {
  batches,
  selectedBatch,
  selectedBatchId,
  selectedItems,
  loadingBatches,
  loadingBatch,
  requestError,
  actionLoading,
  uploadTransferLocked,
  refreshBatches,
  refreshBatch,
  selectBatch,
  resolveItem,
  bulkResolve,
  cancel,
  markLegacyComplete,
  dismissBatch,
  resetState
} = useBulkCvBatches();
const { showDefaultToast, showConfirmModal } = useToast();
const { connectionState, startRealtime, stopRealtime } = useBulkCvRealtime();

const TERMINAL_BATCH_STATUSES = [
  'completed',
  'completed_with_errors',
  'cancelled',
  'failed'
];
const PROCESSING_STATUSES = [
  'waiting_for_upload',
  'uploading',
  'uploaded',
  'queued',
  'parsing',
  'candidate_match_check',
  'creating_candidate',
  'updating_candidate',
  'creating_applicant',
  'updating_existing_applicant'
];
const SAFE_BULK_ACTIONS = [
  BULK_CV_ACTIONS.OVERRIDE,
  BULK_CV_ACTIONS.DUPLICATE,
  BULK_CV_ACTIONS.USE_EXISTING,
  BULK_CV_ACTIONS.UPDATE_APPLICANT,
  BULK_CV_ACTIONS.KEEP_APPLICANT,
  BULK_CV_ACTIONS.SKIP
];
const actionLabels = {
  [BULK_CV_ACTIONS.OVERRIDE]: 'Overwrite selected',
  [BULK_CV_ACTIONS.DUPLICATE]: 'Create duplicates',
  [BULK_CV_ACTIONS.USE_EXISTING]: 'Use existing candidates',
  [BULK_CV_ACTIONS.UPDATE_APPLICANT]: 'Update existing applicants',
  [BULK_CV_ACTIONS.KEEP_APPLICANT]: 'Keep existing applicants',
  [BULK_CV_ACTIONS.SKIP]: 'Skip selected'
};

const TRAY_HIDDEN_STORAGE_KEY = 'bulk-cv-tray-hidden';
const minimized = ref(true);
const trayHidden = ref(true);
const activeFilter = ref('all');
const itemSearch = ref('');
const selectedItemIds = ref([]);
const reviewingItem = ref(null);
const showResolutionModal = ref(false);
const showCompletionModal = ref(false);
const currentPage = ref(1);
const pageSize = 20;
const previousBatchStatuses = reactive({});
const completionNotified = new Set();

const batchStatusMeta = computed(() => {
  const status = selectedBatch.value?.status;
  const labels = {
    draft: { label: 'Draft', variant: 'secondary' },
    uploading: { label: 'Uploading', variant: 'info' },
    queued: { label: 'Processing', variant: 'info' },
    processing: { label: 'Processing', variant: 'info' },
    action_required: { label: 'Action required', variant: 'warning' },
    completed: { label: 'Completed', variant: 'success' },
    completed_with_errors: {
      label: 'Completed with errors',
      variant: 'warning'
    },
    failed: { label: 'Failed', variant: 'danger' },
    cancelled: { label: 'Cancelled', variant: 'secondary' }
  };
  return (
    labels[status] || {
      label: String(status || 'Unknown').replace(/_/g, ' '),
      variant: 'secondary'
    }
  );
});
const selectedHasJob = computed(() => Boolean(selectedBatch.value?.job?.id));
const selectedProcessingMode = computed(() =>
  selectedHasJob.value
    ? selectedBatch.value?.job?.position || 'Selected job'
    : 'Candidates only'
);

const count = (key) => Number(selectedBatch.value?.counts?.[key] || 0);
const totalCount = computed(() =>
  Number(selectedBatch.value?.counts?.total || selectedItems.value.length)
);
const processingCount = computed(() =>
  PROCESSING_STATUSES.reduce((total, status) => total + count(status), 0)
);
const uploadedCount = computed(() =>
  Math.max(
    0,
    totalCount.value - count('waiting_for_upload') - count('uploading')
  )
);
const resolvedCount = computed(
  () =>
    count('completed') + count('skipped') + count('failed') + count('cancelled')
);
const progressPercent = computed(() =>
  totalCount.value
    ? Math.min(100, Math.round((resolvedCount.value / totalCount.value) * 100))
    : 0
);

const counters = computed(() => [
  { label: 'Total', value: totalCount.value, variant: 'secondary' },
  { label: 'Uploaded', value: uploadedCount.value, variant: 'info' },
  { label: 'Processing', value: processingCount.value, variant: 'info' },
  { label: 'Completed', value: count('completed'), variant: 'success' },
  {
    label: 'Action Required',
    value: count('action_required'),
    variant: 'warning'
  },
  { label: 'Failed', value: count('failed'), variant: 'danger' },
  { label: 'Skipped', value: count('skipped'), variant: 'secondary' }
]);

const outcomeCounters = computed(() => {
  const outcomeKeys = [
    ['New candidates created', 'new_candidates_created'],
    ['Existing candidates updated', 'existing_candidates_updated'],
    ...(selectedHasJob.value
      ? [
          [
            'Existing candidates added to job',
            'existing_candidates_added_to_job'
          ],
          ['Existing applicants updated', 'existing_applicants_updated']
        ]
      : [['Existing candidates reused', 'existing_candidates_reused']]),
    ['Duplicate candidates created', 'duplicate_candidates_created'],
    ['Skipped', 'skipped'],
    ['Failed', 'failed']
  ];
  return outcomeKeys.map(([label, key]) => ({ label, value: count(key) }));
});

const filterOptions = [
  { label: 'All files', value: 'all' },
  { label: 'Action required', value: 'action_required' },
  { label: 'Processing', value: 'processing' },
  { label: 'Completed', value: 'completed' },
  { label: 'Failed', value: 'failed' },
  { label: 'Skipped', value: 'skipped' }
];

const displayBatchStatus = (status) => {
  if (status === 'queued') return 'Processing';
  return String(status || 'Unknown')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());
};

const filteredItems = computed(() => {
  const query = itemSearch.value.trim().toLowerCase();
  return selectedItems.value.filter((item) => {
    const matchesFilter =
      activeFilter.value === 'all' ||
      (activeFilter.value === 'processing'
        ? PROCESSING_STATUSES.includes(item.status)
        : item.status === activeFilter.value);
    const parsed = item.parsed_candidate || {};
    const searchable = [
      item.name,
      parsed.first_name,
      parsed.middle_name,
      parsed.last_name,
      parsed.candidate_email
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return matchesFilter && (!query || searchable.includes(query));
  });
});

const totalPages = computed(() =>
  Math.max(1, Math.ceil(filteredItems.value.length / pageSize))
);
const pagedItems = computed(() => {
  const start = (currentPage.value - 1) * pageSize;
  return filteredItems.value.slice(start, start + pageSize);
});

watch([activeFilter, itemSearch, selectedBatchId], () => {
  currentPage.value = 1;
  selectedItemIds.value = [];
});

const selectedDecisionItems = computed(() =>
  selectedItems.value.filter((item) =>
    selectedItemIds.value.includes(String(item.id))
  )
);

watch(
  () =>
    selectedItems.value
      .map(
        (item) =>
          `${item.id}:${item.status}:${item.scenario || ''}:` +
          `${item.condition_key || ''}:${(item.allowed_actions || []).join(
            ','
          )}`
      )
      .join('|'),
  () => {
    const stillEligible = selectedItems.value.filter(
      (item) =>
        selectedItemIds.value.includes(String(item.id)) &&
        item.status === 'action_required' &&
        item.scenario !== 'multiple_matches' &&
        Boolean(item.condition_key) &&
        Boolean(item.allowed_actions?.length)
    );
    const condition = stillEligible[0]?.condition_key;
    selectedItemIds.value = stillEligible
      .filter((item) => item.condition_key === condition)
      .map((item) => String(item.id));
  }
);

const availableBulkActions = computed(() => {
  if (!selectedDecisionItems.value.length) return [];
  const common = selectedDecisionItems.value.reduce(
    (actions, item) =>
      actions.filter((action) => item.allowed_actions.includes(action)),
    SAFE_BULK_ACTIONS.slice()
  );
  return common.filter((action) => actionLabels[action]);
});

const similarDecisionItems = computed(() => {
  const condition = selectedDecisionItems.value[0]?.condition_key;
  if (!condition) return [];
  return selectedItems.value.filter(
    (item) =>
      item.status === 'action_required' &&
      item.scenario !== 'multiple_matches' &&
      item.condition_key === condition &&
      item.allowed_actions?.length
  );
});

const selectAllSimilar = () => {
  selectedItemIds.value = similarDecisionItems.value.map((item) =>
    String(item.id)
  );
};

const toggleSelection = (item) => {
  const id = String(item.id);
  if (selectedItemIds.value.includes(id)) {
    selectedItemIds.value = selectedItemIds.value.filter(
      (selectedId) => selectedId !== id
    );
    return;
  }
  const existing = selectedDecisionItems.value[0];
  if (existing && existing.condition_key !== item.condition_key) {
    showDefaultToast(
      'Different duplicate condition',
      'warning',
      'Bulk actions can only combine CVs with the same candidate match condition and processing mode.'
    );
    return;
  }
  selectedItemIds.value = selectedItemIds.value.concat(id);
};

const reviewItem = (item) => {
  reviewingItem.value = item;
  showResolutionModal.value = true;
};

const onResolveItem = async (decision) => {
  if (!reviewingItem.value) return;
  try {
    await resolveItem(reviewingItem.value, decision.action, decision);
    showDefaultToast(
      'Decision saved',
      'success',
      selectedHasJob.value
        ? 'The candidate was resolved before the applicant relationship was processed.'
        : 'The candidate-only decision was saved. No applicant was created.'
    );
    showResolutionModal.value = false;
    reviewingItem.value = null;
    emitter.emit('reload-candidate-list');
  } catch (error) {
    if ([404, 409].includes(error?.response?.status) && reviewingItem.value) {
      try {
        const itemId = reviewingItem.value.id;
        await refreshBatch(reviewingItem.value.batch_id, { quiet: true });
        reviewingItem.value =
          selectedItems.value.find(
            (candidate) => String(candidate.id) === String(itemId)
          ) || null;
        if (!reviewingItem.value) showResolutionModal.value = false;
      } catch (_) {
        // The original server error remains the most useful message.
      }
    }
    showDefaultToast(
      'Decision failed',
      'danger',
      error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.message ||
        'The decision could not be saved.'
    );
  }
};

const runBulkAction = (action) => {
  const items = selectedDecisionItems.value.slice();
  const warning =
    action === BULK_CV_ACTIONS.DUPLICATE
      ? !selectedHasJob.value
        ? 'This creates separate candidate profiles for every selected CV even though candidate matches exist. No applicants will be created.'
        : items[0]?.scenario === 'existing_applicant'
        ? 'These candidates already have applicants for the selected job. Continuing creates separate candidate profiles and additional applicants for every selected CV.'
        : 'This creates separate candidate profiles and applicants for every selected CV even though candidate matches exist.'
      : `Apply “${actionLabels[action]}” to ${items.length} CVs with the same duplicate condition?`;
  showConfirmModal(
    {
      message: warning,
      actionButton: {
        variant: action === BULK_CV_ACTIONS.DUPLICATE ? 'danger' : 'primary',
        text: 'Confirm decision'
      },
      returnButton: { variant: 'secondary', text: 'Go back' }
    },
    async (confirmed) => {
      if (!confirmed) return;
      try {
        const response = await bulkResolve(
          selectedBatch.value.id,
          items,
          action
        );
        const results = Array.isArray(response?.results)
          ? response.results
          : [];
        const failedResults = results.filter(
          (result) => result.status === 'error' || result.error
        );
        selectedItemIds.value = failedResults.map((result) =>
          String(result.item_id)
        );
        if (failedResults.length) {
          const completed = Math.max(0, items.length - failedResults.length);
          showDefaultToast(
            'Bulk decision partially completed',
            'warning',
            `${completed} CV${completed === 1 ? '' : 's'} resolved; ${
              failedResults.length
            } still need attention. ${
              failedResults[0]?.error?.message || 'Review the selected items.'
            }`
          );
        } else {
          showDefaultToast(
            'Bulk decision saved',
            'success',
            `${items.length} CVs were resolved with the selected decision.`
          );
        }
        if (failedResults.length < items.length) {
          emitter.emit('reload-candidate-list');
        }
      } catch (error) {
        showDefaultToast(
          'Bulk decision failed',
          'danger',
          error?.response?.data?.message ||
            error?.message ||
            'Please try again.'
        );
      }
    }
  );
};

const onCancel = () => {
  if (!selectedBatch.value) return;
  showConfirmModal(
    {
      message:
        'Cancel this batch? Files already resolved remain unchanged; pending items will stop.',
      actionButton: { variant: 'danger', text: 'Cancel batch' },
      returnButton: { variant: 'secondary', text: 'Keep processing' }
    },
    async (confirmed) => {
      if (!confirmed) return;
      try {
        await cancel(selectedBatch.value);
        showDefaultToast(
          'Batch cancelled',
          'success',
          'Processing was cancelled.'
        );
      } catch (error) {
        showDefaultToast(
          'Cancellation failed',
          'danger',
          error?.response?.data?.message ||
            error?.message ||
            'Please try again.'
        );
      }
    }
  );
};

const onLegacyComplete = async () => {
  try {
    const response = await markLegacyComplete();
    if (response?.code && Number(response.code) !== 0) {
      throw new Error(response.message || 'The queue cannot be completed yet.');
    }
    showDefaultToast(
      'Legacy queue completed',
      'success',
      response?.message || 'Done.'
    );
  } catch (error) {
    showDefaultToast(
      'Unable to complete queue',
      'danger',
      error?.response?.data?.message || error?.message || 'Please try again.'
    );
  }
};

const canCancelSelected = computed(() => {
  if (!selectedBatch.value) return false;
  if (TERMINAL_BATCH_STATUSES.includes(selectedBatch.value.status))
    return false;
  return (
    selectedBatch.value.is_legacy ||
    selectedBatch.value.allowed_actions.includes('cancel')
  );
});

const canClearSelected = computed(
  () =>
    Boolean(selectedBatch.value) &&
    !selectedBatch.value.is_legacy &&
    TERMINAL_BATCH_STATUSES.includes(selectedBatch.value.status) &&
    selectedBatch.value.allowed_actions.includes('dismiss')
);

const onClearSelected = () => {
  if (!canClearSelected.value) return;
  const batch = selectedBatch.value;
  showConfirmModal(
    {
      message: selectedHasJob.value
        ? 'Clear this completed batch from your tray? Candidate and applicant records and the audit history remain safely stored.'
        : 'Clear this completed batch from your tray? Candidate records and the audit history remain safely stored.',
      actionButton: { variant: 'primary', text: 'Clear from tray' },
      returnButton: { variant: 'secondary', text: 'Keep batch' }
    },
    async (confirmed) => {
      if (!confirmed) return;
      try {
        await dismissBatch(batch);
        showCompletionModal.value = false;
        if (!batches.value.length) hideTray();
        showDefaultToast(
          'Batch cleared',
          'success',
          'The finished batch was removed from the tray. Its audit history was not deleted.'
        );
      } catch (error) {
        showDefaultToast(
          'Unable to clear batch',
          'danger',
          error?.response?.data?.message ||
            error?.response?.data?.detail ||
            error?.message ||
            'Please try again.'
        );
      }
    }
  );
};

const selectBatchFromInput = async (event) => {
  try {
    await selectBatch(event.target.value);
  } catch (error) {
    showDefaultToast(
      'Unable to open batch',
      'danger',
      error?.response?.data?.message || error?.message || 'Please try again.'
    );
  }
};

const refreshSelected = async () => {
  try {
    if (selectedBatchId.value) await refreshBatch(selectedBatchId.value);
    else await refreshBatches();
  } catch (_) {
    // The visible REST error banner provides a non-repeating failure message.
  }
};

const preventReloadDuringUpload = (event) => {
  if (!uploadTransferLocked.value) return;
  event.preventDefault();
  event.returnValue = '';
};

watch(
  () => selectedBatchId.value,
  () => {
    selectedItemIds.value = [];
  }
);

watch(uploadTransferLocked, (locked) => {
  if (locked) {
    minimized.value = false;
    trayHidden.value = false;
    window.localStorage.removeItem(TRAY_HIDDEN_STORAGE_KEY);
    emitter.emit('bulk-cv-tray-visibility', true);
    window.addEventListener('beforeunload', preventReloadDuringUpload);
  } else {
    window.removeEventListener('beforeunload', preventReloadDuringUpload);
  }
});

watch(
  () => batches.value.map((batch) => `${batch.id}:${batch.status}`).join('|'),
  () => {
    const hasActiveWork = batches.value.some(
      (batch) => !TERMINAL_BATCH_STATUSES.includes(batch.status)
    );
    const explicitlyHidden =
      window.localStorage.getItem(TRAY_HIDDEN_STORAGE_KEY) === '1';
    // Initial hydration can fail while the immediate REST/WebSocket recovery
    // succeeds. Surface recovered active work automatically unless the user
    // deliberately hid the tray.
    if (hasActiveWork && !explicitlyHidden && trayHidden.value) {
      trayHidden.value = false;
      emitter.emit('bulk-cv-tray-visibility', true);
    }
    batches.value.forEach((batch) => {
      const id = String(batch.id);
      const previous = previousBatchStatuses[id];
      previousBatchStatuses[id] = batch.status;
      if (
        previous &&
        !TERMINAL_BATCH_STATUSES.includes(previous) &&
        TERMINAL_BATCH_STATUSES.includes(batch.status) &&
        !completionNotified.has(id)
      ) {
        completionNotified.add(id);
        if (String(selectedBatchId.value) === id)
          showCompletionModal.value = true;
        showDefaultToast(
          'Bulk CV parsing completed',
          batch.status === 'completed' ? 'success' : 'warning',
          `${batch.name} has finished. Open the summary for full results.`
        );
        emitter.emit('reload-candidate-list');
      }
    });
  }
);

const onBatchStarted = () => {
  trayHidden.value = false;
  window.localStorage.removeItem(TRAY_HIDDEN_STORAGE_KEY);
  minimized.value = false;
  emitter.emit('bulk-cv-tray-visibility', true);
  refreshSelected();
};

const hideTray = () => {
  if (uploadTransferLocked.value) return;
  trayHidden.value = true;
  window.localStorage.setItem(TRAY_HIDDEN_STORAGE_KEY, '1');
  emitter.emit('bulk-cv-tray-visibility', false);
};

const showTray = () => {
  trayHidden.value = false;
  minimized.value = false;
  window.localStorage.removeItem(TRAY_HIDDEN_STORAGE_KEY);
  emitter.emit('bulk-cv-tray-visibility', true);
  refreshSelected();
};

const openBatchHistory = async () => {
  try {
    await refreshBatches({ includeCompleted: true });
  } catch (_) {
    // The shared request error is rendered if history could not be loaded.
  }
  if (!batches.value.length) {
    showDefaultToast(
      'No bulk CV batches',
      'info',
      'There are no active or recent bulk CV parsing batches.'
    );
    return;
  }
  showTray();
};

const toggleTray = () => {
  if (trayHidden.value) openBatchHistory();
  else hideTray();
};

const toggleMinimized = () => {
  if (!uploadTransferLocked.value) minimized.value = !minimized.value;
};

const publishTrayVisibility = () => {
  emitter.emit('bulk-cv-tray-visibility', !trayHidden.value);
};

onMounted(async () => {
  // The composable is a module singleton so the tray survives route changes.
  // Logout clears localStorage and reloads the application; on a normal browser
  // refresh retain the selected batch so the user returns to the same work.
  resetState({ preserveSelection: true });
  try {
    await refreshBatches();
  } catch (_) {
    // Error is shown in the tray; active work is checked again on the next poll.
  }
  const explicitlyHidden =
    window.localStorage.getItem(TRAY_HIDDEN_STORAGE_KEY) === '1';
  const hasActiveWork = batches.value.some(
    (batch) => !TERMINAL_BATCH_STATUSES.includes(batch.status)
  );
  trayHidden.value = explicitlyHidden || !hasActiveWork;
  await startRealtime();
  publishTrayVisibility();
  emitter.on('bulk-batch-started', onBatchStarted);
  emitter.on('open-bulk-cv-batches', openBatchHistory);
  emitter.on('toggle-bulk-cv-tray', toggleTray);
  emitter.on('bulk-cv-tray-state-request', publishTrayVisibility);
});

onBeforeUnmount(() => {
  stopRealtime();
  window.removeEventListener('beforeunload', preventReloadDuringUpload);
  emitter.off('bulk-batch-started', onBatchStarted);
  emitter.off('open-bulk-cv-batches', openBatchHistory);
  emitter.off('toggle-bulk-cv-tray', toggleTray);
  emitter.off('bulk-cv-tray-state-request', publishTrayVisibility);
});
</script>

<template>
  <Teleport to="body">
    <div
      v-if="batches.length && !trayHidden"
      class="bulk-parsing-container shadow-small bg-card d-flex flex-column"
      :class="{ minimized }"
    >
      <component
        :is="uploadTransferLocked ? 'div' : 'button'"
        :type="uploadTransferLocked ? undefined : 'button'"
        class="w-100 parsing-header bg-color-18 dark-bg-color-19 text-start border-0"
        :class="{ 'upload-transfer-locked': uploadTransferLocked }"
        @click="toggleMinimized"
      >
        <div class="flex-between">
          <div class="min-width-0">
            <div class="d-flex align-items-center">
              <span class="fw-bold text-color-2">Bulk CV parsing</span>
              <span
                v-if="selectedBatch"
                class="badge bulk-status-pill ms-2"
                :class="`badge-outline-${batchStatusMeta.variant}`"
              >
                {{ batchStatusMeta.label }}
              </span>
            </div>
            <div
              v-if="selectedBatch"
              class="text-11px text-color-2 text-truncate"
            >
              {{ selectedProcessingMode }} · {{ resolvedCount }}/{{
                totalCount
              }}
              resolved
            </div>
          </div>
          <div class="flex-start ms-3 me-4">
            <span
              class="connection-dot me-2"
              :class="connectionState === 'live' ? 'live' : 'polling'"
              :title="
                connectionState === 'live'
                  ? 'Live updates connected'
                  : 'REST polling active'
              "
            ></span>
            <span
              v-if="uploadTransferLocked"
              class="d-flex align-items-center text-color-2"
              title="The tray stays open until every selected file is received by the server"
            >
              <FeatherIcon
                type="lock"
                size="15"
                class="me-1"
              />
              <span class="text-11px fw-bold">Uploading</span>
            </span>
            <FeatherIcon
              v-else
              :type="minimized ? 'maximize-2' : 'minimize-2'"
              size="19"
              class="text-color-2"
            />
          </div>
        </div>
      </component>
      <button
        v-if="!uploadTransferLocked"
        type="button"
        class="tray-close-button flex-center"
        aria-label="Close bulk CV parsing tray"
        title="Close tray"
        @click.stop="hideTray"
      >
        <FeatherIcon
          type="x"
          size="19"
        />
      </button>

      <div
        v-if="!minimized"
        class="parsing-body overflow-auto flex-1 border"
      >
        <div class="sticky-tools bg-card border-bottom p-3">
          <div class="d-flex flex-wrap align-items-end gap-2">
            <div class="batch-selector flex-1">
              <label
                for="bulk-cv-batch-selector"
                class="text-11px text-muted"
              >
                Active or recent batch
              </label>
              <select
                id="bulk-cv-batch-selector"
                class="form-select py-2"
                :value="selectedBatchId"
                @change="selectBatchFromInput"
              >
                <option
                  v-for="batch in batches"
                  :key="batch.id"
                  :value="batch.id"
                >
                  {{ batch.name
                  }}{{
                    batch.job?.position ? ` · ${batch.job.position}` : ''
                  }}
                  · {{ displayBatchStatus(batch.status) }}
                </option>
              </select>
            </div>
            <button
              type="button"
              class="btn btn-outline-info"
              :disabled="loadingBatch || loadingBatches"
              aria-label="Refresh bulk CV batch"
              title="Refresh from server"
              @click="refreshSelected"
            >
              <FeatherIcon
                type="refresh-cw"
                size="15"
                :animation="loadingBatch || loadingBatches ? 'spin' : undefined"
              />
            </button>
          </div>

          <div
            v-if="requestError"
            class="alert alert-danger py-2 px-3 mt-2 mb-0"
          >
            <span class="text-12px">{{ requestError }}</span>
          </div>

          <template v-if="selectedBatch">
            <div class="batch-heading mt-3 flex-between gap-3">
              <div class="min-width-0">
                <div class="fw-bold text-truncate">
                  {{ selectedBatch.name }}
                </div>
                <div class="text-12px text-muted">
                  {{ selectedProcessingMode }}
                  <span v-if="selectedBatch.reference">
                    · Ref {{ selectedBatch.reference }}
                  </span>
                </div>
              </div>
              <div class="flex-end flex-shrink-0">
                <button
                  v-if="TERMINAL_BATCH_STATUSES.includes(selectedBatch.status)"
                  class="btn btn-sm btn-outline-success me-2"
                  @click="showCompletionModal = true"
                >
                  Parsing Summary
                </button>
                <button
                  v-if="canClearSelected"
                  class="btn btn-sm btn-outline-secondary me-2"
                  :disabled="
                    Boolean(actionLoading[`dismiss-${selectedBatch.id}`])
                  "
                  title="Remove this finished batch from the tray without deleting its audit history"
                  @click="onClearSelected"
                >
                  <FeatherIcon
                    type="check-circle"
                    size="13"
                    class="me-1"
                  />
                  Clear
                </button>
                <button
                  v-if="canCancelSelected"
                  class="btn btn-sm btn-outline-danger"
                  @click="onCancel"
                >
                  Cancel
                </button>
                <button
                  v-if="
                    selectedBatch.is_legacy &&
                    selectedItems.length &&
                    selectedItems.every((item) =>
                      ['completed', 'failed', 'skipped'].includes(item.status)
                    )
                  "
                  class="btn btn-sm btn-outline-success"
                  @click="onLegacyComplete"
                >
                  Mark complete
                </button>
              </div>
            </div>

            <div
              class="progress mt-3"
              style="height: 8px"
            >
              <div
                class="progress-bar bg-success"
                role="progressbar"
                :style="{ width: `${progressPercent}%` }"
                :aria-valuenow="progressPercent"
                aria-valuemin="0"
                aria-valuemax="100"
              ></div>
            </div>
            <div class="counter-grid mt-3">
              <div
                v-for="counter in counters"
                :key="counter.label"
                class="counter-card rounded border p-2"
              >
                <div
                  class="text-18px fw-bold"
                  :class="`text-${counter.variant}`"
                >
                  {{ counter.value }}
                </div>
                <div class="text-10px text-muted text-uppercase">
                  {{ counter.label }}
                </div>
              </div>
            </div>
          </template>
        </div>

        <div
          v-if="selectedBatch"
          class="p-3"
        >
          <div class="d-flex flex-wrap gap-2 align-items-center mb-3">
            <select
              v-model="activeFilter"
              class="form-select filter-select py-2"
            >
              <option
                v-for="filter in filterOptions"
                :key="filter.value"
                :value="filter.value"
              >
                {{ filter.label }}
              </option>
            </select>
            <div class="position-relative flex-1">
              <FeatherIcon
                type="search"
                size="14"
                class="search-icon text-muted"
              />
              <input
                v-model="itemSearch"
                type="search"
                class="form-control py-2 search-input"
                placeholder="Search file or parsed candidate"
              />
            </div>
          </div>

          <div
            v-if="selectedDecisionItems.length"
            class="bulk-decision-bar rounded border p-2 mb-3"
          >
            <div class="flex-between flex-wrap gap-2">
              <div>
                <div class="fw-bold text-12px">
                  {{ selectedDecisionItems.length }} matching-condition CVs
                  selected
                </div>
                <div class="text-10px text-muted">
                  {{ selectedDecisionItems[0]?.condition_key }}
                </div>
              </div>
              <div class="d-flex flex-wrap gap-1">
                <button
                  v-if="
                    similarDecisionItems.length > selectedDecisionItems.length
                  "
                  class="btn btn-sm btn-outline-secondary"
                  @click="selectAllSimilar"
                >
                  Select all {{ similarDecisionItems.length }} similar
                </button>
                <button
                  v-for="action in availableBulkActions"
                  :key="action"
                  class="btn btn-sm btn-outline-info"
                  :disabled="actionLoading[`batch-${selectedBatch.id}`]"
                  @click="runBulkAction(action)"
                >
                  {{ actionLabels[action] }}
                </button>
                <button
                  class="btn btn-sm btn-outline-secondary"
                  @click="selectedItemIds = []"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>

          <div
            v-if="loadingBatch && !selectedItems.length"
            class="py-5 flex-center"
          >
            <Spinner />
          </div>
          <div
            v-else-if="!pagedItems.length"
            class="empty-state rounded border p-4 text-center"
          >
            <FeatherIcon
              type="inbox"
              size="28"
              class="text-muted mb-2"
            />
            <div class="fw-bold">No CVs in this view</div>
            <div class="text-12px text-muted">
              Try another status or search term.
            </div>
          </div>
          <template v-else>
            <ParsingItem
              v-for="item in pagedItems"
              :key="item.id"
              :item="item"
              :batch="selectedBatch"
              :selected="selectedItemIds.includes(String(item.id))"
              :loading="Boolean(actionLoading[item.id])"
              @review="reviewItem"
              @toggle-selection="toggleSelection"
            />
          </template>

          <div
            v-if="totalPages > 1"
            class="d-flex justify-content-between align-items-center mt-3"
          >
            <button
              class="btn btn-sm btn-outline-secondary"
              :disabled="currentPage === 1"
              @click="currentPage -= 1"
            >
              Previous
            </button>
            <span class="text-12px"
              >Page {{ currentPage }} of {{ totalPages }}</span
            >
            <button
              class="btn btn-sm btn-outline-secondary"
              :disabled="currentPage === totalPages"
              @click="currentPage += 1"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>

  <BulkCvResolutionModal
    v-model="showResolutionModal"
    :item="reviewingItem"
    :batch="selectedBatch"
    :loading="Boolean(reviewingItem && actionLoading[reviewingItem.id])"
    @resolve="onResolveItem"
  />

  <CustomModal
    v-model="showCompletionModal"
    title="Parsing Summary"
    root-class="bulk-cv-modal-layer"
    backdrop
    width-class="col-xl-6 col-lg-8 col-12"
  >
    <div
      v-if="selectedBatch"
      class="p-4"
    >
      <div class="rounded border p-3 mb-3 bg-body">
        <div class="text-12px text-muted">Processing mode</div>
        <div class="fw-bold text-18px">
          {{ selectedProcessingMode }}
        </div>
        <div class="text-12px mt-1">
          {{ totalCount }} CVs processed · {{ batchStatusMeta.label }}
        </div>
      </div>
      <div class="summary-grid">
        <div
          v-for="outcome in outcomeCounters"
          :key="outcome.label"
          class="rounded border p-3"
        >
          <div class="text-22px fw-bold">{{ outcome.value }}</div>
          <div class="text-12px text-muted">{{ outcome.label }}</div>
        </div>
      </div>
    </div>
  </CustomModal>
</template>

<style lang="scss" scoped>
.bulk-parsing-container {
  position: fixed;
  bottom: 0;
  right: 64px;
  width: min(760px, calc(100vw - 80px));
  max-height: calc(100vh - 96px);
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  z-index: 2999;
}

.bulk-parsing-container.minimized {
  width: min(520px, calc(100vw - 80px));
}

.tray-close-button {
  position: absolute;
  top: 8px;
  right: 10px;
  z-index: 2;
  width: 30px;
  height: 30px;
  padding: 0;
  color: #fff;
  background: #202633;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 6px;
  transition: background 0.15s ease, border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.tray-close-button:hover {
  color: #fff;
  background: #0f141d;
  border-color: rgba(255, 255, 255, 0.7);
}

.tray-close-button:focus-visible {
  outline: 0;
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.32);
}

.parsing-header {
  padding: 12px 16px;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  border: 1px solid #3b3f5c !important;
}

.parsing-body {
  min-height: 260px;
}

.sticky-tools {
  position: sticky;
  top: 0;
  z-index: 3;
}

.batch-selector {
  min-width: 280px;
}

.filter-select {
  width: 170px;
}

.counter-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(72px, 1fr));
  gap: 6px;
}

.counter-card {
  min-width: 0;
}

.connection-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

.connection-dot.live {
  background: var(--bs-success);
}

.connection-dot.polling {
  background: var(--bs-warning);
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
}

.search-input {
  padding-left: 34px !important;
}

.bulk-decision-bar {
  border-left: 3px solid var(--bs-info) !important;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.min-width-0 {
  min-width: 0;
}

@media screen and (max-width: 900px) {
  .counter-grid {
    grid-template-columns: repeat(4, minmax(72px, 1fr));
  }
}

@media screen and (max-width: 576px) {
  .bulk-parsing-container,
  .bulk-parsing-container.minimized {
    right: 1%;
    width: 98%;
    max-height: calc(100vh - 72px);
  }

  .counter-grid {
    grid-template-columns: repeat(2, minmax(72px, 1fr));
  }

  .batch-selector,
  .filter-select {
    width: 100%;
    min-width: 100%;
  }

  .summary-grid {
    grid-template-columns: 1fr;
  }
}
</style>

<!-- CustomModal is teleported outside this component. Keep both the parsing
     decision modal and completion summary above the fixed tray (z-index 2999). -->
<style lang="scss">
.bulk-cv-modal-layer .custom-modal-overlay {
  z-index: 3098 !important;
  opacity: 0.42 !important;
}

.bulk-cv-modal-layer .custom-modal {
  z-index: 3100 !important;
}
</style>
